import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import {
  Code,
  Copy,
  ExternalLink,
  Globe,
  Zap,
  Terminal,
  Book,
  Layout,
  Calendar,
  Sparkles,
  Users,
  LayoutGrid,
  MonitorSmartphone,
  Info
} from 'lucide-react';

export default function AdminWidgets() {
  const [dashboardWidth, setDashboardWidth] = useState('100%');
  const [dashboardHeight, setDashboardHeight] = useState('800');
  const [selectedRestaurantId, setSelectedRestaurantId] = useState('{RESTAURANT_ID}');

  const PRODUCTION_URL = 'https://okreserva.okgo.mx';

  const dashboardIframeCode = `<iframe
  src="${PRODUCTION_URL}/embed/dashboard/${selectedRestaurantId}"
  width="${dashboardWidth}"
  height="${dashboardHeight}px"
  frameborder="0"
  style="border: none; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
  title="OkReserva Dashboard"
></iframe>`;

  const widgetIframeCode = `<iframe
  src="${PRODUCTION_URL}/widget/${selectedRestaurantId}"
  width="100%"
  height="600px"
  frameborder="0"
  style="border: none; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
  allow="microphone"
  title="OkReserva Reservaciones"
></iframe>`;

  const eventWidgetIframeCode = `<iframe
  src="${PRODUCTION_URL}/event-public/${selectedRestaurantId}"
  width="100%"
  height="800px"
  frameborder="0"
  style="border: none; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
  title="OkReserva Eventos"
></iframe>`;

  const experienceWidgetIframeCode = `<iframe
  src="${PRODUCTION_URL}/widget/${selectedRestaurantId}/experiencias"
  width="100%"
  height="800px"
  frameborder="0"
  style="border: none; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
  title="OkReserva Experiencias"
></iframe>`;

  const clientDashboardWidgetCode = `<iframe
  src="${PRODUCTION_URL}/clientes/{NICKNAME}/dashboard?embed=true"
  width="100%"
  height="900px"
  frameborder="0"
  style="border: none; border-radius: 16px; box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);"
  title="OkReserva Cliente Dashboard"
></iframe>`;

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  const CodeBlock = ({ code, label, onCopy }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-gray-700">{label}</Label>
        <Button variant="outline" size="sm" onClick={onCopy} className="border-orange-200 text-orange-600 hover:bg-orange-50">
          <Copy className="h-3 w-3 mr-1" /> Copiar
        </Button>
      </div>
      <Textarea
        readOnly
        value={code}
        className="font-mono text-xs bg-gray-900 text-green-400 border-gray-700 rounded-xl min-h-[140px] resize-none"
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-100 rounded-xl">
            <MonitorSmartphone className="h-6 w-6 text-orange-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Widgets de Integración</h1>
            <p className="text-sm text-gray-500">Códigos para embeber OkReserva en sitios externos</p>
          </div>
        </div>
      </div>

      <div className="p-6 max-w-5xl mx-auto space-y-6">
        {/* Restaurant ID Config */}
        <Card className="border-orange-200 bg-orange-50/30">
          <CardContent className="pt-5">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-1">
                <Label className="text-sm font-semibold text-gray-700">ID del Restaurante</Label>
                <Input
                  value={selectedRestaurantId}
                  onChange={(e) => setSelectedRestaurantId(e.target.value)}
                  placeholder="Ingresa el ID del restaurante"
                  className="mt-1 rounded-xl border-orange-200 focus:ring-orange-500"
                />
              </div>
              <div className="flex items-start gap-2 text-sm text-orange-700 bg-orange-100 px-3 py-2 rounded-xl max-w-xs">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Obtén el ID desde la sección de Restaurantes</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="dashboard" className="space-y-4">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-6 bg-gray-100 p-1 rounded-xl">
            <TabsTrigger value="dashboard" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
              <Layout className="h-3.5 w-3.5 mr-1" /> Dashboard
            </TabsTrigger>
            <TabsTrigger value="reservas" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
              <Calendar className="h-3.5 w-3.5 mr-1" /> Reservas
            </TabsTrigger>
            <TabsTrigger value="eventos" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Eventos
            </TabsTrigger>
            <TabsTrigger value="experiencias" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
              <Zap className="h-3.5 w-3.5 mr-1" /> Experiencias
            </TabsTrigger>
            <TabsTrigger value="clientes" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
              <Users className="h-3.5 w-3.5 mr-1" /> Clientes
            </TabsTrigger>
            <TabsTrigger value="api" className="rounded-lg text-xs data-[state=active]:bg-white data-[state=active]:text-orange-600 data-[state=active]:shadow-sm">
              <Terminal className="h-3.5 w-3.5 mr-1" /> API
            </TabsTrigger>
          </TabsList>

          {/* Dashboard */}
          <TabsContent value="dashboard">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Layout className="h-5 w-5 text-orange-500" />
                  <CardTitle className="text-base text-gray-900">Dashboard del Restaurante</CardTitle>
                </div>
                <CardDescription>Embebe el dashboard completo dentro de tu plataforma (Bubble.io, WordPress, etc.)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm text-gray-600">Ancho</Label>
                    <Input value={dashboardWidth} onChange={(e) => setDashboardWidth(e.target.value)} placeholder="100% o 1200px" className="mt-1 rounded-xl" />
                  </div>
                  <div>
                    <Label className="text-sm text-gray-600">Alto (px)</Label>
                    <Input type="number" value={dashboardHeight} onChange={(e) => setDashboardHeight(e.target.value)} placeholder="800" className="mt-1 rounded-xl" />
                  </div>
                </div>
                <CodeBlock
                  code={dashboardIframeCode}
                  label="Código Iframe"
                  onCopy={() => copyToClipboard(dashboardIframeCode, 'Código iframe Dashboard')}
                />
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  <strong>Nota:</strong> El dashboard embebido requiere sesión activa del restaurante. Para Bubble.io usa autenticación por token.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reservas */}
          <TabsContent value="reservas">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Globe className="h-5 w-5 text-orange-500" />
                  <CardTitle className="text-base text-gray-900">Widget de Reservaciones</CardTitle>
                </div>
                <CardDescription>Widget público para hacer reservaciones desde el sitio web del restaurante</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <CodeBlock
                  code={widgetIframeCode}
                  label="Código Iframe de Reservaciones"
                  onCopy={() => copyToClipboard(widgetIframeCode, 'Código iframe Reservaciones')}
                />
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                  <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2"><Book className="h-4 w-4" /> Instrucciones</h4>
                  <ol className="text-sm text-gray-600 space-y-1.5 list-decimal pl-4">
                    <li>Reemplaza <code className="bg-white border px-1 rounded text-orange-600">{'{RESTAURANT_ID}'}</code> con el ID real</li>
                    <li>Copia el código y pégalo en el HTML de tu sitio web</li>
                    <li>El widget cargará los horarios y mesas configurados automáticamente</li>
                  </ol>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Eventos */}
          <TabsContent value="eventos">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-orange-500" />
                  <CardTitle className="text-base text-gray-900">Widget de Eventos</CardTitle>
                </div>
                <CardDescription>Muestra eventos activos del restaurante y permite compra de tickets</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <CodeBlock
                  code={eventWidgetIframeCode}
                  label="Código Iframe de Eventos"
                  onCopy={() => copyToClipboard(eventWidgetIframeCode, 'Código iframe Eventos')}
                />
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                  Muestra todos los eventos activos con opción de comprar tickets directamente desde el iframe.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Experiencias */}
          <TabsContent value="experiencias">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-orange-500" />
                  <CardTitle className="text-base text-gray-900">Widget de Experiencias</CardTitle>
                </div>
                <CardDescription>Muestra experiencias gastronómicas y permite reservaciones directas</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <CodeBlock
                  code={experienceWidgetIframeCode}
                  label="Código Iframe de Experiencias"
                  onCopy={() => copyToClipboard(experienceWidgetIframeCode, 'Código iframe Experiencias')}
                />
                <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-600">
                  Muestra todas las experiencias activas con descripción, precios y opción de reservar.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Clientes */}
          <TabsContent value="clientes">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-orange-500" />
                  <CardTitle className="text-base text-gray-900">Dashboard de Clientes</CardTitle>
                </div>
                <CardDescription>Embebe el portal de clientes en sitios externos</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <CodeBlock
                  code={clientDashboardWidgetCode}
                  label="Código Iframe Dashboard Cliente"
                  onCopy={() => copyToClipboard(clientDashboardWidgetCode, 'Código iframe Cliente')}
                />
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800">
                  <strong>Nota:</strong> Reemplaza <code className="bg-white border px-1 rounded">{'{NICKNAME}'}</code> con el OkUser del cliente. Permite ver reservaciones, eventos y experiencias desde tu plataforma.
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API */}
          <TabsContent value="api">
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    <CardTitle className="text-base text-gray-900">API REST para Plataformas Externas</CardTitle>
                  </div>
                  <CardDescription>Endpoints públicos para integrar OkReserva (Bubble.io, WordPress, etc.)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">Base URL (Producción)</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm font-mono text-gray-800 truncate">{PRODUCTION_URL}/api</code>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(`${PRODUCTION_URL}/api`, 'Base URL')}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div className="p-3 bg-gray-50 border border-gray-200 rounded-xl">
                      <p className="text-xs text-gray-500 mb-1">Restaurant ID actual</p>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-sm font-mono text-orange-600 truncate">{selectedRestaurantId}</code>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(selectedRestaurantId, 'Restaurant ID')}>
                          <Copy className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <h4 className="font-semibold text-blue-800 flex items-center gap-2 mb-3">
                      <ExternalLink className="h-4 w-4" /> Configuración en Bubble.io
                    </h4>
                    <ol className="text-sm text-blue-700 space-y-2">
                      {[
                        'Ve a Plugins → API Connector e instálalo',
                        'Crea una nueva API llamada "OkReserva"',
                        'En Authentication, selecciona "None or self-handled"',
                        'Agrega los endpoints con los ejemplos de abajo'
                      ].map((step, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="bg-blue-200 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base text-gray-900">Endpoints Disponibles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    {
                      method: 'GET',
                      path: '/api/public/restaurants/{restaurant_id}/availability',
                      desc: 'Obtener disponibilidad de mesas por fecha',
                      params: '?date=YYYY-MM-DD&party_size=4',
                      color: 'bg-green-500'
                    },
                    {
                      method: 'POST',
                      path: '/api/widget/reservations',
                      desc: 'Crear una nueva reservación',
                      params: 'JSON body con datos de reserva',
                      color: 'bg-blue-500'
                    },
                    {
                      method: 'GET',
                      path: '/api/public/restaurants/{restaurant_id}/sections',
                      desc: 'Obtener secciones/zonas del restaurante',
                      params: 'Ninguno',
                      color: 'bg-green-500'
                    },
                    {
                      method: 'GET',
                      path: '/api/public/restaurants/{restaurant_id}/events',
                      desc: 'Obtener eventos públicos del restaurante',
                      params: 'Ninguno',
                      color: 'bg-green-500'
                    },
                  ].map((ep, i) => (
                    <div key={i} className="border border-gray-200 rounded-xl overflow-hidden">
                      <div className="px-4 py-3 bg-gray-50 flex items-center gap-3">
                        <Badge className={cn("font-mono text-white text-xs", ep.color)}>{ep.method}</Badge>
                        <code className="text-sm font-mono text-gray-700 flex-1 truncate">{ep.path}</code>
                        <Button variant="ghost" size="sm" className="h-7" onClick={() => copyToClipboard(`${PRODUCTION_URL}${ep.path}`, ep.desc)}>
                          <Copy className="h-3 w-3 mr-1" /> Copiar
                        </Button>
                      </div>
                      <div className="px-4 py-2 text-sm text-gray-600">
                        <span className="font-medium">{ep.desc}</span>
                        <span className="text-gray-400 ml-2">— {ep.params}</span>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
